import { HeartHandshake, ExternalLink, Cross, Home, Package, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const causes = [
  {
    name: "American Red Cross",
    desc: "Support disaster relief, blood donation, and emergency care for families in need across the country.",
    url: "https://www.redcross.org/donate/donation.html",
    icon: Cross,
    tint: "bg-[#C8102E]",
    tag: "Emergency & blood services",
  },
  {
    name: "Ronald McDonald House — Orange County",
    desc: "Keeps families close to their hospitalized children with a home away from home. A cause close to every NICU family's heart.",
    url: "https://rmhcsc.org/orangecounty",
    icon: Home,
    tint: "bg-[#D4AF37]",
    tag: "Families near their kids",
  },
];

export default function Donate() {
  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-12">
      <div className="flex items-center gap-3 mb-2">
        <HeartHandshake className="text-[#6A0DAD]" size={30} />
        <h1 className="font-display text-4xl md:text-5xl text-[#1A202C]">Give Back</h1>
      </div>
      <p className="text-[#4A5568] mb-8 max-w-2xl">
        These organizations walk alongside NICU and medically complex families. If you're able,
        a donation helps another family feel a little less alone.
      </p>

      <div className="grid md:grid-cols-2 gap-4">
        {causes.map((c) => (
          <div key={c.name} data-testid={`donate-${c.name.toLowerCase().split(" ")[0]}`} className="bg-white border border-[#6A0DAD] rounded-2xl p-7 shadow-sm hover:shadow-md transition-all flex flex-col">
            <span className={`grid place-items-center w-12 h-12 rounded-xl ${c.tint} text-white mb-4`}><c.icon size={22} /></span>
            <span className="text-xs font-heading uppercase tracking-widest text-[#B8972E] mb-1">{c.tag}</span>
            <h2 className="font-heading text-xl font-semibold text-[#1A202C]">{c.name}</h2>
            <p className="text-[#4A5568] mt-2 leading-relaxed flex-1">{c.desc}</p>
            <a
              href={c.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`donate-link-${c.name.toLowerCase().split(" ")[0]}`}
              className="mt-5 inline-flex items-center justify-center gap-2 bg-[#6A0DAD] text-white hover:bg-[#58098F] rounded-full px-6 py-3 font-heading font-semibold transition-colors"
            >
              Donate <ExternalLink size={16} />
            </a>
          </div>
        ))}
      </div>

      <div className="mt-6 bg-[#6A0DAD]/8 border border-[#6A0DAD]/30 rounded-2xl p-6 text-center">
        <p className="text-[#4A5568] leading-relaxed">
          Donations are made directly on each organization's official website. AMIR is not affiliated
          with these charities — we simply love and stand behind the work they do for families like ours. 💜
        </p>
      </div>

      {/* Share Extra Medical Supplies */}
      <section className="mt-12">
        <div className="flex items-center gap-3 mb-2">
          <Package className="text-[#6A0DAD]" size={26} />
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A202C]">Share Extra Medical Supplies</h2>
        </div>
        <p className="text-[#4A5568] mb-6 max-w-2xl leading-relaxed">
          Insurance often sends more than we can use. If you have unopened, unexpired supplies you don't
          need, pass them on to a family who does — and if you're running low, just ask. No money, only kindness.
        </p>

        <div className="bg-white border border-[#6A0DAD] rounded-2xl p-7 shadow-sm">
          <p className="text-xs font-heading uppercase tracking-widest text-[#B8972E] mb-3">Things families often share</p>
          <div className="flex flex-wrap gap-2 mb-6">
            {["Gloves", "2x2 gauze", "Trach ties / neckties", "Suction catheters", "Feeding tubes & extensions", "Syringes", "HMEs", "Tape & dressings", "Wipes"].map((s) => (
              <span key={s} data-testid={`supply-tag-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="px-3 py-1.5 rounded-full bg-[#6A0DAD]/8 text-[#6A0DAD] text-sm font-heading">
                {s}
              </span>
            ))}
          </div>

          <div className="flex flex-wrap gap-3">
            <Link
              to="/help"
              data-testid="donate-offer-supplies-btn"
              className="inline-flex items-center gap-2 bg-[#6A0DAD] text-white hover:bg-[#58098F] rounded-full px-6 py-3 font-heading font-semibold transition-colors"
            >
              I have supplies to give <ArrowRight size={16} />
            </Link>
            <Link
              to="/help"
              data-testid="donate-need-supplies-btn"
              className="inline-flex items-center gap-2 bg-white border border-[#6A0DAD] text-[#6A0DAD] hover:shadow-md rounded-full px-6 py-3 font-heading font-semibold transition-all"
            >
              I need supplies
            </Link>
          </div>
          <p className="text-xs text-[#4A5568] mt-4 leading-relaxed">
            Posts go to the <Link to="/help" className="text-[#6A0DAD] font-semibold">Help Board</Link>, where you can
            add details and connect. Please only share unopened, in-date supplies, and always follow your care team's guidance.
          </p>
        </div>
      </section>
    </div>
  );
}
